/**
 * Custom Hook: useStoredItemsPerPageSync
 *
 * Automatically syncs a paginated page component with its persisted page size
 * preference from localStorage on mount.
 *
 * This hook:
 * 1. Reads the stored page size from localStorage
 * 2. Compares it with current Redux state
 * 3. If different, updates Redux with the stored preference
 * 4. Optionally resets pagination range to first page
 *
 * Usage:
 * ```
 * useStoredItemsPerPageSync({
 *   storageKey: PAGINATION_STORAGE_KEYS.users,
 *   itemsPerPage,
 *   rangeKey: "userRange",
 *   onSync: updateUsers,
 * });
 * ```
 */

import { useEffect } from 'react';
import {
    getInitialPaginationRange,
    getStoredItemsPerPage,
} from '../paginationPreferences.js';

/**
 * Syncs a paginated screen with its persisted page size on mount.
 *
 * @param {Object} params
 * @param {string} params.storageKey - localStorage key for this component (from PAGINATION_STORAGE_KEYS)
 * @param {number} params.itemsPerPage - Current page size from Redux state
 * @param {Function} params.onSync - Redux update callback (e.g., updateUsers)
 * @param {string} [params.rangeKey] - Optional range field to reset (e.g., "userRange")
 * @param {string} [params.fieldName="itemsPerPage"] - Redux field name to update
 * @param {number} [params.fallback] - Fallback page size when nothing is stored
 *
 * @example
 * // In UsersPage component:
 * useStoredItemsPerPageSync({
 *   storageKey: PAGINATION_STORAGE_KEYS.users,
 *   itemsPerPage,
 *   rangeKey: "userRange",
 *   onSync: updateUsers,
 * });
 * // On mount, if localStorage has 50 but Redux has 20:
 * // → Calls updateUsers({ itemsPerPage: 50, userRange: [0, 49], currentPage: 0 })
 */
export function useStoredItemsPerPageSync({
    storageKey,
    itemsPerPage,
    onSync,
    rangeKey,
    fieldName = 'itemsPerPage',
    fallback,
}) {
    useEffect(() => {
        const saved = getStoredItemsPerPage(storageKey, fallback);

        // Already synced - no action needed
        if (saved === itemsPerPage) {
            return;
        }

        // Build update payload
        const updates = { [fieldName]: saved };

        // Optionally reset range to first page with new page size
        if (rangeKey) {
            updates[rangeKey] = getInitialPaginationRange(saved);
            updates.currentPage = 0;
        }

        // Update Redux once
        onSync(updates);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Run only on mount
}
